// src/components/Target/TargetWidget.jsx
// Add this to your dashboard/overview page
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../constants/roles';
import targetService from '../../services/target.service';
import SetTargetModal from './SetTargetModal';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

// Roles that CAN assign targets to others
const CAN_ASSIGN_ROLES = [
  ROLES.SUPER_ADMIN, ROLES.OFFICE_ADMIN, ROLES.ADMIN,
  ROLES.ZONAL_MANAGER, ROLES.AREA_MANAGER,
  ROLES.EXECUTIVE, ROLES.SALES_OFFICER,
];

const fmt = (a) => `₹${Number(a||0).toLocaleString('en-IN', { minimumFractionDigits:0, maximumFractionDigits:0 })}`;

// ── Progress Bar ──────────────────────────────────────────────────────────────
const ProgressBar = ({ pct, achieved, target }) => {
  const color = pct >= 100 ? '#2e7d32' : pct >= 75 ? '#4caf50' : pct >= 50 ? '#ff9800' : '#f44336';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
        <span style={{ fontSize:12, color:'#6c757d' }}>
          {fmt(achieved)} achieved
        </span>
        <span style={{ fontSize:12, fontWeight:700, color }}>
          {pct}%
        </span>
      </div>
      <div style={{ background:'#e9ecef', borderRadius:20, height:10, overflow:'hidden' }}>
        <div style={{
          width:`${Math.min(pct, 100)}%`, height:'100%', borderRadius:20,
          background: pct >= 100 ? '#2e7d32' : pct >= 75 ? '#4caf50' : pct >= 50 ? '#ff9800' : '#f44336',
          transition:'width 0.5s ease',
        }}/>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', marginTop:4 }}>
        <span style={{ fontSize:11, color:'#adb5bd' }}>₹0</span>
        <span style={{ fontSize:11, color:'#adb5bd' }}>{fmt(target)}</span>
      </div>
    </div>
  );
};

// ── Main Widget ───────────────────────────────────────────────────────────────
const TargetWidget = () => {
  const { user } = useAuth();
  const canAssign = CAN_ASSIGN_ROLES.includes(user?.role);

  const now = new Date();
  const [month,        setMonth]        = useState(now.getMonth() + 1);
  const [year,         setYear]         = useState(now.getFullYear());
  const [myTarget,     setMyTarget]     = useState(null);
  const [teamTargets,  setTeamTargets]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showModal,    setShowModal]    = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [myRes, teamRes] = await Promise.all([
        targetService.getMyTarget(month, year),
        targetService.getTeamTargets(month, year),
      ]);
      if (myRes.success)   setMyTarget(myRes.data);
      if (teamRes.success) setTeamTargets(teamRes.data || []);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [month, year]);

  return (
    <div style={{ marginBottom: 32 }}>

      {/* Widget Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <h5 style={{ margin:0, fontSize:18, fontWeight:700, color:'#1a1a1a' }}>🎯 Sales Target</h5>
          <p style={{ margin:'2px 0 0 0', fontSize:13, color:'#6c757d' }}>
            {MONTHS[month-1]} {year}
          </p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {/* Month/Year selector */}
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
            style={{ padding:'6px 10px', border:'1px solid #dee2e6', borderRadius:8, fontSize:13, outline:'none' }}>
            {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            style={{ padding:'6px 10px', border:'1px solid #dee2e6', borderRadius:8, fontSize:13, outline:'none' }}>
            {[2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {/* Set Target button */}
          {canAssign && (
            <button onClick={() => setShowModal(true)}
              style={{ padding:'7px 16px', background:'#4caf50', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              + Set Target
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'#adb5bd' }}>Loading targets...</div>
      ) : (
        <>
          {/* ── My Target Card ── */}
          <div style={{
            background: myTarget?.targetAmount > 0 ? 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)' : '#f8f9fa',
            border: `1px solid ${myTarget?.targetAmount > 0 ? '#a5d6a7' : '#dee2e6'}`,
            borderRadius:12, padding:'20px 24px', marginBottom:20,
          }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
              <div>
                <p style={{ margin:0, fontSize:12, fontWeight:600, color:'#6c757d', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  My Target — {MONTHS[month-1]} {year}
                </p>
                {myTarget?.targetAmount > 0 ? (
                  <p style={{ margin:'4px 0 0 0', fontSize:28, fontWeight:800, color:'#2e7d32' }}>
                    {fmt(myTarget.targetAmount)}
                  </p>
                ) : (
                  <p style={{ margin:'4px 0 0 0', fontSize:16, color:'#adb5bd' }}>No target set for this month</p>
                )}
              </div>
              {myTarget?.targetAmount > 0 && (
                <div style={{ textAlign:'right' }}>
                  <p style={{ margin:0, fontSize:12, color:'#6c757d' }}>Achieved</p>
                  <p style={{ margin:0, fontSize:22, fontWeight:700, color: myTarget.percentage >= 100 ? '#2e7d32' : '#1565c0' }}>
                    {fmt(myTarget.achieved)}
                  </p>
                </div>
              )}
            </div>
            {myTarget?.targetAmount > 0 && (
              <ProgressBar
                pct={myTarget.percentage}
                achieved={myTarget.achieved}
                target={myTarget.targetAmount}
              />
            )}
            {myTarget?.target?.notes && (
              <p style={{ margin:'12px 0 0 0', fontSize:12, color:'#6c757d', fontStyle:'italic' }}>
                📝 {myTarget.target.notes}
              </p>
            )}
          </div>

          {/* ── Team Targets ── */}
          {teamTargets.length > 0 && (
            <div>
              <p style={{ margin:'0 0 12px 0', fontSize:14, fontWeight:700, color:'#1a1a1a' }}>
                Team Targets
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {teamTargets.map((t, idx) => {
                  const name    = `${t.user?.firstName || ''} ${t.user?.lastName || ''}`.trim();
                  const role    = t.user?.role?.displayName || t.user?.role?.name || '';
                  const target  = parseFloat(t.targetAmount || 0);
                  const achieved= parseFloat(t.achieved || 0);
                  const pct     = t.percentage || 0;
                  const color   = pct >= 100 ? '#2e7d32' : pct >= 75 ? '#4caf50' : pct >= 50 ? '#ff9800' : '#f44336';

                  return (
                    <div key={idx} style={{
                      background:'#fff', border:'1px solid #e9ecef', borderRadius:10,
                      padding:'14px 18px',
                    }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: target > 0 ? 10 : 0 }}>
                        <div>
                          <p style={{ margin:0, fontWeight:600, fontSize:14 }}>{name || '—'}</p>
                          <p style={{ margin:0, fontSize:11, color:'#6c757d' }}>{role}</p>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          {target > 0 ? (
                            <>
                              <p style={{ margin:0, fontSize:13, color:'#6c757d' }}>
                                {fmt(achieved)} / {fmt(target)}
                              </p>
                              <p style={{ margin:0, fontSize:14, fontWeight:700, color }}>
                                {pct}%
                              </p>
                            </>
                          ) : (
                            <span style={{ fontSize:12, color:'#adb5bd', fontStyle:'italic' }}>
                              No target set
                            </span>
                          )}
                        </div>
                      </div>
                      {target > 0 && (
                        <div style={{ background:'#e9ecef', borderRadius:20, height:6, overflow:'hidden' }}>
                          <div style={{ width:`${Math.min(pct,100)}%`, height:'100%', borderRadius:20, background:color, transition:'width 0.5s' }}/>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Set Target Modal */}
      {showModal && (
        <SetTargetModal
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetchData(); }}
        />
      )}
    </div>
  );
};

export default TargetWidget;